import getContentType from './get-content-type';

/**
 * Fetch bulk entries from Contentstack by UIDs (Client-side only)
 * Calls Contentstack REST API directly
 * @param entryUIDs - Array of entry UIDs to fetch
 * @returns Array of course entries
 */
export const fetchBulkEntries = async (entryUIDs: string[]) => {
  if (!entryUIDs || entryUIDs.length === 0) {
    return [[]];
  }

  try {
    const apiKey = process.env.NEXT_PUBLIC_API_KEY || '';
    const deliveryToken = process.env.NEXT_PUBLIC_DELIVERY_TOKEN || '';
    const environment = process.env.NEXT_PUBLIC_ENVIRONMENT || '';

    if (!apiKey || !deliveryToken || !environment) {
      throw new Error('Contentstack credentials are missing');
    }

    // Build query with $in operator for bulk fetching
    const query = JSON.stringify({
      uid: { $in: entryUIDs },
    });

    // Include references for chapters.lessons.lessons to calculate duration
    // Contentstack REST API uses include[] parameter for nested references
    const includeReferences = ['chapters.lessons.lessons'];
    const referencesParam = includeReferences.map(ref => `include[]=${encodeURIComponent(ref)}`).join('&');

    const url = `https://cdn.contentstack.io/v3/content_types/course_details/entries?environment=${encodeURIComponent(environment)}&query=${encodeURIComponent(query)}${referencesParam ? `&${referencesParam}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        api_key: apiKey,
        access_token: deliveryToken,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch bulk entries');
    }

    const data = await response.json();
    const entries = data.entries || [];

    // Add type information for course_details: use content_type taxonomy for standalone, else Course
    const coursesWithType = entries.map((course: any) => {
      const displayType = course.is_standalone
        ? (getContentType(course.taxonomies || [], 'content_type') || 'Content')
        : 'Course';
      course['type'] = displayType;
      return course;
    });

    return [coursesWithType];
  } catch (error) {
    return [[]];
  }
};

/**
 * Fetch bulk entries by entry UIDs for a specific content type (Client-side only)
 * Calls Contentstack REST API directly
 * @param entryUIDs - Array of entry UIDs to fetch
 * @param contentType - Content type UID (e.g., 'lessons')
 * @returns Array of entries
 */
export const fetchBulkEntriesByEntryUids = async (
  entryUIDs: string[],
  contentType: string
) => {
  if (!entryUIDs || entryUIDs.length === 0) {
    return [];
  }

  try {
    const apiKey = process.env.NEXT_PUBLIC_API_KEY || '';
    const deliveryToken = process.env.NEXT_PUBLIC_DELIVERY_TOKEN || '';
    const environment = process.env.NEXT_PUBLIC_ENVIRONMENT || '';

    if (!apiKey || !deliveryToken || !environment) {
      throw new Error('Contentstack credentials are missing');
    }

    // Build query with $in operator for bulk fetching
    const query = JSON.stringify({
      uid: { $in: entryUIDs },
    });

    const url = `https://cdn.contentstack.io/v3/content_types/${encodeURIComponent(contentType)}/entries?environment=${encodeURIComponent(environment)}&query=${encodeURIComponent(query)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        api_key: apiKey,
        access_token: deliveryToken,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch bulk entries by UIDs');
    }

    const data = await response.json();
    const entries = data.entries || [];

    return Array.isArray(entries) ? entries : [entries];
  } catch (error) {
    return [];
  }
};
